import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Users,
  ListTodo,
} from 'lucide-react';
import type { Task } from '@/types/task';
import { demoAssignees } from '@/data/demo-tasks';

interface TaskDetailSubtasksProps {
  readonly task: Task;
  readonly onTaskUpdate: (task: Task) => void;
  isEditMode?: boolean;
}

export function TaskDetailSubtasks({ task, onTaskUpdate, isEditMode = false }: Readonly<TaskDetailSubtasksProps>) {
  const [newSubtask, setNewSubtask] = useState({ title: '', assigneeId: '', dueDate: '' });

  const handleAddSubtask = () => {
    if (newSubtask.title.trim() && newSubtask.assigneeId) {
      const assignee = demoAssignees.find(a => a.id === newSubtask.assigneeId);
      const subtask = {
        id: `sub-${Date.now()}`,
        title: newSubtask.title.trim(),
        status: 'New' as const,
        assigneeId: newSubtask.assigneeId,
        assigneeName: assignee?.name ?? '',
        dueDate: newSubtask.dueDate ?? undefined,
      };
      
      onTaskUpdate({
        ...task,
        subtasks: [...(task.subtasks ?? []), subtask],
      });
      
      setNewSubtask({ title: '', assigneeId: '', dueDate: '' });
    }
  };

  const handleSubtaskStatusChange = (subtaskId: string, status: string) => {
    const updatedSubtasks = (task.subtasks ?? []).map(sub => {
      if (sub.id === subtaskId) {
        return { ...sub, status: status as Task['status'] };
      }
      return sub;
    });
    onTaskUpdate({ ...task, subtasks: updatedSubtasks });
  };

  const handleDeleteSubtask = (subtaskId: string) => {
    onTaskUpdate({
      ...task,
      subtasks: (task.subtasks ?? []).filter(sub => sub.id !== subtaskId),
    });
  };

  const completedSubtasks = (task.subtasks ?? []).filter(s => s.status === 'Resolved' || s.status === 'Closed').length;
  const totalSubtasks = task.subtasks?.length ?? 0;
  const subtaskProgress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0;

  return (
    <div className={`grid grid-cols-1 gap-6 ${isEditMode ? 'lg:grid-cols-3' : ''}`}>
      {/* Left Column - Subtasks List (2/3 width) */}
      <div className={isEditMode ? 'lg:col-span-2' : ''}>
        {/* R4.3: Subtasks with Roll-up Accuracy */}
        <Card className="border border-border/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ListTodo className="h-5 w-5" />
                  Subtasks
                </CardTitle>
                <CardDescription className="text-xs">
                  Breakdown of work into smaller tasks - {completedSubtasks} of {totalSubtasks} completed
                </CardDescription>
              </div>
              {totalSubtasks > 0 && (
                <div className="text-sm font-medium">{Math.round(subtaskProgress)}%</div>
              )}
            </div>
            {totalSubtasks > 0 && (
              <>
                <Progress value={subtaskProgress} className="mt-2" />
                {/* R4.3: Status-Based Indicators */}
                <div className="mt-3 space-y-1">
                  {completedSubtasks === totalSubtasks && totalSubtasks > 0 && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>✓ All subtasks complete ({totalSubtasks} of {totalSubtasks}) 🎯 Ready to close</span>
                    </div>
                  )}
                  {(task.subtasks ?? []).some(s => s.status === 'Blocked') && (
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                      <AlertCircle className="h-4 w-4" />
                      <span>⚠️ {(task.subtasks ?? []).filter(s => s.status === 'Blocked').length} subtask(s) blocked - Click to view</span>
                    </div>
                  )}
                  {completedSubtasks > 0 && completedSubtasks < totalSubtasks && (
                    <div className="text-xs text-muted-foreground">
                      Progress: {Math.round(subtaskProgress)}% ({completedSubtasks} of {totalSubtasks} complete) • 
                      {' '}{(task.subtasks ?? []).filter(s => s.status === 'Resolved' || s.status === 'Closed').length} Done • 
                      {' '}{(task.subtasks ?? []).filter(s => s.status === 'In Progress').length} In Progress • 
                      {' '}{(task.subtasks ?? []).filter(s => s.status === 'New').length} Todo
                    </div>
                  )}
                </div>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-3">
            {(task.subtasks ?? []).length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No subtasks yet</p>
                <p className="text-sm mt-1">Add subtasks using the form on the right</p>
              </div>
            ) : (
              (task.subtasks ?? []).map((subtask) => {
                const assignee = demoAssignees.find(a => a.id === subtask.assigneeId);
                return (
                  <div
                    key={subtask.id}
                    className={cn(
                      'flex items-center gap-3 p-3 border rounded-lg hover:bg-accent transition-colors group',
                      assignee?.type === 'user' && 'bg-blue-50/50 border-blue-100',
                      assignee?.type === 'group' && 'bg-purple-50/50 border-purple-100'
                    )}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{subtask.title}</span>
                        <Badge
                          variant="outline"
                          className={
                            subtask.status === 'Resolved' || subtask.status === 'Closed'
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : ''
                          }
                        >
                          {subtask.status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {assignee && (
                          <div className="flex items-center gap-1">
                            {assignee.type === 'user' ? (
                              <>
                                <Avatar className="h-5 w-5">
                                  <AvatarImage src={assignee.avatar} />
                                  <AvatarFallback>{assignee.name[0]}</AvatarFallback>
                                </Avatar>
                                <span className="text-blue-700">👤 {assignee.name}</span>
                              </>
                            ) : (
                              <>
                                <Users className="h-4 w-4 text-purple-600" />
                                <span className="text-purple-700">👥 {assignee.name}</span>
                              </>
                            )}
                          </div>
                        )}
                        {subtask.dueDate && (
                          <span>Due {new Date(subtask.dueDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Select
                        value={subtask.status}
                        onValueChange={(status: string) => handleSubtaskStatusChange(subtask.id, status)}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="New">New</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                          <SelectItem value="Resolved">Resolved</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteSubtask(subtask.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column - Add Subtask (1/3 width) */}
      {isEditMode && (
      <div className="lg:col-span-1">
        <Card className="sticky top-4 border border-border/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Plus className="h-4 w-4" />
              Add Subtask
            </CardTitle>
            <CardDescription className="text-xs">Create a new subtask</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Subtask Title</label>
              <Textarea
                placeholder="Enter subtask title..."
                value={newSubtask.title}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewSubtask({ ...newSubtask, title: e.target.value })}
                rows={3}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assignee</label>
              <Select
                value={newSubtask.assigneeId}
                onValueChange={(value: string) => setNewSubtask({ ...newSubtask, assigneeId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee..." />
                </SelectTrigger>
                <SelectContent>
                  {demoAssignees.map((assignee) => (
                    <SelectItem key={assignee.id} value={assignee.id}>
                      {assignee.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Due Date (Optional)</label>
              <Input
                type="date"
                value={newSubtask.dueDate}
                onChange={(e) => setNewSubtask({ ...newSubtask, dueDate: e.target.value })}
              />
            </div>
            <Button 
              onClick={handleAddSubtask} 
              className="w-full"
              disabled={!newSubtask.title.trim() || !newSubtask.assigneeId}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Subtask
            </Button>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
